<template>
  <div class="user-status-badge">
    <template v-if="status === USER_STATUS_DEMO">
      <m-icon
        type="dot"
        size="28"
        :color="iconColor"
        :style="{ width: '1.6rem', marginRight: '.4rem', marginLeft: '.6rem' }"
      />
      <div>{{ $t(`userList.${status}`) }}</div>
    </template>
    <m-dropdown
      v-else
      :title="$t('userList.status')"
      close-on-click
      @click.stop
    >
      <m-btn
        hide-border
        xs
      >
        <m-icon
          type="dot"
          size="28"
          :color="iconColor"
          :style="{ width: '1.6rem', marginRight: '.4rem' }"
        />
        {{ $t(`userList.${status}`) }}
      </m-btn>
      <template #overlay>
        <m-card list>
          <user-confirm-invite-action
            v-if="![USER_STATUS_ACTIVE, USER_STATUS_ARCHIVED].includes(status)"
            :user="props.user"
          />
          <user-archive-action :user="props.user" />
          <m-divider xxs />
          <m-card-item
            class="_status-details"
            :clickable="false"
            light
            @click.stop
          >
            <template v-if="status === USER_STATUS_ARCHIVED">
              {{ $t('userList.deactivatedOn') }} {{ formatDate(props.user.archivedAt) }}
            </template>
            <template v-if="status === USER_STATUS_ACTIVE">
              {{ $t('userList.activatedAt') }} {{ formatDate(props.user.activatedAt) }}
            </template>
            <template v-if="status === USER_STATUS_INVITE_PENDING">
              {{ invitedByAt }} {{ formatDate(props.user.invitation.createdAt) }}
            </template>
          </m-card-item>
        </m-card>
      </template>
    </m-dropdown>
  </div>
</template>

<script setup>
import UserArchiveAction from '@/components/UserArchiveAction.vue';
import UserConfirmInviteAction from '@/components/UserConfirmInviteAction.vue';
import colors from 'shared/colors';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useUsers from '@/composables/user/users';
import { DateTime } from 'luxon';
import { USER_STATUS_ACTIVE, USER_STATUS_ARCHIVED, USER_STATUS_DEMO, USER_STATUS_INVITE_PENDING, USER_STATUS_NO_INVITATION } from '@/lib/constants';
import { computed } from 'vue';
import { displayName } from 'shared/composables/user';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  users: {
    type: Array,
    required: true,
  },
});

const { loggedInUser } = useLoggedInUser();
const { t } = useI18n();
const { getActivationStatus } = useUsers();

const formatDate = (date) => {
  switch (typeof (date)) {
    case 'object':
      return DateTime.fromObject(date).toLocaleString(DateTime.DATE_SHORT);
    default:
      return DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT);
  }
};

const invitedByAt = computed(() => {
  const actor = props.users.find((u) => u.uid === props.user.invitation.actor.uid);
  if (actor === undefined) {
    return t('userList.invitedAt');
  }
  if (actor.uid === loggedInUser.value.uid) {
    return t('userList.invitedByYouAt');
  }
  return t('userList.invitedByAt', { name: displayName(actor) });
});

const status = computed(() => getActivationStatus(props.user));

const iconColor = computed(() => {
  switch (status.value) {
    case USER_STATUS_ACTIVE:
      return colors.green.base;
    case USER_STATUS_ARCHIVED:
      return colors.grey.base;
    case USER_STATUS_INVITE_PENDING:
      return colors.yellow.base;
    case USER_STATUS_NO_INVITATION:
      return colors.red.base;
    default:
      return '';
  }
});
</script>

<style scoped lang="scss">
.user-status-badge {
  display: flex;
  flex-direction: row;
  align-items: center;
}

._status-details {
  font-size: $font-size-2;
}
</style>
